/**
 * Provysn SSO Review Page
 *
 * SSO Admin view to review all SSO configuration requests across all instances.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { authFetchInit, getAuthHeaders, hasAuthSession } from '../utils/authSession';
import { listingTableSx } from '../components/listingTableSx';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  IconButton,
  Tooltip,
  Alert,
  Divider,
  Stack,
  Collapse,
  Menu,
  Checkbox,
  FormControlLabel,
  ListItemText,
  Grid,
  Card,
  useTheme as useMuiTheme,
} from '@mui/material';
import {
  Security as SecurityIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  CloudUpload as CloudUploadIcon,
  ExpandMore as ExpandMoreIcon,
  ViewColumn as ViewColumnIcon,
  Info as InfoIcon,
  PersonOutline as PersonOutlineIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { API_ROOT } from '../config/apiConfig';
import { ssoAPI } from '../services/api';
import { UI_TOKENS } from '../constants/uiTokens';
import SSOConfigCompactDetails from '../components/SSOConfigCompactDetails';
import { ListingPagination, ListingSortSelect } from '../components/ListingPagination';
import { DEFAULT_LISTING_SORT, ListingSortValue, sortListing } from '../utils/listingSort';
import OIDCCredentialsDialog from '../components/OIDCCredentialsDialog';
import RenewClientSecretControl, { formatSecretExpiry } from '../components/RenewClientSecretControl';
import { chipSxFromSurface, getProtocolSurface, getRiskSurface, getStatusSurface } from '../utils/appearanceTokens';

// ---------------------------------------------------------------------------
// SSO Progress Stepper
// ---------------------------------------------------------------------------

const SSO_STEPS = [
  { label: 'Submitted',    sub: 'Config submitted for review' },
  { label: 'Under Review', sub: 'SSO admin reviewing' },
  { label: 'Approved',     sub: 'Configuration approved' },
  { label: 'Onboarded to IdP', sub: 'App created in IdP' },
];

const getSSOStepIndex = (status: string) => {
  switch (status) {
    case 'Pending Review':   return 0;
    case 'In Progress':
    case 'Need More Info':
    case 'Rejected':         return 1;
    case 'Approved':         return 2;
    case 'Completed':        return 3;
    default:                 return 0;
  }
};

const SSOProgressStepper: React.FC<{ status: string }> = ({ status }) => {
  const activeStep  = getSSOStepIndex(status);
  const isRejected  = status === 'Rejected';

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
      {SSO_STEPS.map((step, idx) => {
        const done     = idx < activeStep;
        const current  = idx === activeStep;
        const rejected = isRejected && idx === 1;
        const future   = idx > activeStep;
        const circleColor = rejected ? '#ef4444' : done || current ? '#6366f1' : 'transparent';
        const borderColor = future ? '#d1d5db' : rejected ? '#ef4444' : '#6366f1';
        const textColor   = future ? '#9ca3af' : rejected ? '#ef4444' : '#1f2937';

        return (
          <React.Fragment key={step.label}>
            {idx > 0 && (
              <Box sx={{ flex: 1, height: 2, mt: '13px', bgcolor: idx <= activeStep && !isRejected ? '#6366f1' : isRejected && idx <= 1 ? '#ef4444' : '#e5e7eb' }} />
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 72 }}>
              <Box sx={{
                width: 26, height: 26, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: circleColor, border: `2px solid ${borderColor}`,
                color: done || current ? '#fff' : borderColor,
                fontSize: UI_TOKENS.text.labelXs, fontWeight: 700,
                boxShadow: current ? `0 0 0 4px ${rejected ? '#fee2e2' : '#e0e7ff'}` : 'none',
              }}>
                {done && !isRejected
                  ? <CheckCircleIcon sx={{ fontSize: '0.9rem', color: '#fff' }} />
                  : rejected
                  ? <CancelIcon sx={{ fontSize: '0.9rem', color: '#fff' }} />
                  : idx + 1}
              </Box>
              <Typography sx={{ fontSize: UI_TOKENS.text.helper, fontWeight: 700, mt: 0.75, color: textColor, textAlign: 'center', whiteSpace: 'nowrap' }}>
                {rejected ? 'Rejected' : step.label}
              </Typography>
              <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: '#9ca3af', textAlign: 'center', lineHeight: 1.3 }}>
                {rejected ? 'Config rejected' : step.sub}
              </Typography>
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

interface SSOConfig {
  id: string;
  application_id: string;
  application_name: string;
  instance_id: string;
  instance_name: string;
  enterprise_id: string;
  submitted_by: string;
  submitted_by_name: string;
  sso_type: 'SAML' | 'OIDC';
  platform: string;
  status: string;
  connection_name: string;
  contact_email: string;
  entity_id?: string;
  acs_url?: string;
  client_id?: string;
  redirect_uris?: string[];
  scopes?: string[];
  grant_type?: string;
  attributes_to_release?: string[];
  assigned_to?: string;
  assigned_to_name?: string;
  review_notes?: string;
  business_justification?: string;
  data_classification?: string;
  expected_user_count?: number;
  technical_notes?: string;
  risk_level?: string;
  environment?: string;
  // Credentials stored after Entra/Azure provisioning
  client_secret?: string;
  client_secret_expires_at?: string;
  azure_metadata_url?: string;
  metadata_url?: string;
  oidc_discovery_endpoint?: string;
  idp_initiated_url?: string;
  certificate_expiration_date?: string;
  okta_app_id?: string;
  entra_app_id?: string;
  attribute_statements?: any[];
  manual_config?: { client_id?: string; client_secret?: string; client_secret_expires_at?: string; okta_app_id?: string; entra_app_id?: string; oidc_discovery_endpoint?: string; [key: string]: any };
  created_at: string;
  reviewed_at?: string;
}

interface SSOAdmin {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'Pending Review', label: 'Pending Review' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Rejected', label: 'Rejected' },
  { value: 'Need More Info', label: 'Need More Info' },
  { value: 'Completed', label: 'Completed' },
];

const SSO_TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'SAML', label: 'SAML' },
  { value: 'OIDC', label: 'OIDC' },
];

type ColumnKey =
  | 'date'
  | 'application'
  | 'instance'
  | 'environment'
  | 'ssoType'
  | 'connection'
  | 'requestedBy'
  | 'assignedTo'
  | 'secretExpires'
  | 'status'
  | 'actions';

const ALL_COLUMNS: { key: ColumnKey; label: string; width: string; alwaysOn?: boolean }[] = [
  { key: 'date', label: 'Date', width: '8%' },
  { key: 'application', label: 'Application', width: '11%' },
  { key: 'instance', label: 'Instance', width: '8%' },
  { key: 'environment', label: 'Environment', width: '7%' },
  { key: 'ssoType', label: 'SSO Type', width: '6%' },
  { key: 'connection', label: 'Connection Name', width: '10%' },
  { key: 'requestedBy', label: 'Requested By', width: '8%' },
  { key: 'assignedTo', label: 'Assigned To', width: '8%' },
  { key: 'secretExpires', label: 'Secret Expires', width: '12%' },
  { key: 'status', label: 'Status', width: '8%' },
  { key: 'actions', label: 'Actions', width: '14%', alwaysOn: true },
];

const DEFAULT_VISIBLE_COLUMNS: ColumnKey[] = ALL_COLUMNS.map((c) => c.key);

function protocolChipSx(type: string, mode: 'light' | 'dark') {
  return chipSxFromSurface(getProtocolSurface(type, mode));
}

function environmentChipSx(environment: string, mode: 'light' | 'dark') {
  const value = environment.toLowerCase();
  const kind = value === 'production' ? 'success' : value === 'staging' ? 'warning' : 'neutral';
  const surface = getStatusSurface(kind, mode);
  return { bgcolor: surface.bg, color: surface.fg, border: `1px solid ${surface.border}` };
}

function statusChipProps(status: string): {
  label: string;
  color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  variant: 'filled' | 'outlined';
  sx?: object;
} {
  switch (status) {
    case 'Pending Review':
      return { label: 'Pending Review', color: 'warning', variant: 'filled' };
    case 'In Progress':
      return { label: 'In Progress', color: 'primary', variant: 'filled' };
    case 'Approved':
      return { label: 'Approved', color: 'success', variant: 'filled' };
    case 'Rejected':
      return { label: 'Rejected', color: 'error', variant: 'filled' };
    case 'Need More Info':
      return { label: 'Need More Info', color: 'warning', variant: 'outlined' };
    case 'Completed':
      return { label: 'Completed', color: 'success', variant: 'outlined' };
    default:
      return { label: status, color: 'default', variant: 'outlined' };
  }
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return '—'; }
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
}

// ---------------------------------------------------------------------------
// Review Dialog
// ---------------------------------------------------------------------------

interface ReviewDialogProps {
  open: boolean;
  config: SSOConfig | null;
  ssoAdmins: SSOAdmin[];
  onClose: () => void;
  onSubmit: (configId: string, decision: string, notes: string, assignedTo: string) => Promise<void>;
  onProvision?: (configId: string) => Promise<void>;
  submitting: boolean;
  provisioning?: boolean;
  siblingPending?: number;
}

const ReviewDialog: React.FC<ReviewDialogProps> = ({
  open, config, ssoAdmins, onClose, onSubmit, onProvision, submitting, provisioning, siblingPending = 0,
}) => {
  const muiTheme = useMuiTheme();
  const mode = muiTheme.palette.mode;
  const [decision, setDecision] = useState<'' | 'Approved' | 'Rejected' | 'Need More Info' | 'In Progress'>('');
  const [notes, setNotes] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [riskPolicy, setRiskPolicy] = useState<{ policy_name: string; policy_type: string } | null>(null);
  const [showUpdateReview, setShowUpdateReview] = useState(false);
  const [checkedOutSecret, setCheckedOutSecret] = useState('');
  const [checkoutError, setCheckoutError] = useState('');
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    if (open && config) {
      setDecision('');
      setNotes(config.review_notes || '');
      setAssignedTo(config.assigned_to || '');
      setRiskPolicy(null);
      setShowUpdateReview(false);
      setCheckedOutSecret('');
      setCheckoutError('');

      if (config.risk_level && ['Low', 'Medium', 'High', 'Critical'].includes(config.risk_level)) {
        fetch(`${API_ROOT}/okta/policy-mappings`, authFetchInit())
          .then((r) => (r.ok ? r.json() : null))
          .then((data) => {
            if (data) {
              const match = (data.mappings || []).find((m: any) => m.risk_level === config.risk_level);
              setRiskPolicy(match ? { policy_name: match.policy_name, policy_type: match.policy_type } : null);
            }
          })
          .catch(() => {});
      }
    }
  }, [open, config]);

  if (!config) return null;

  const isReviewable = ['Pending Review', 'In Progress', 'Need More Info'].includes(config.status);
  const riskDefined = ['Low', 'Medium', 'High', 'Critical'].includes(String(config.risk_level || ''));
  const needsProvisioning = config.status === 'Approved'
    && !config.client_id
    && !config.manual_config?.client_id
    && !config.azure_metadata_url
    && !config.okta_app_id
    && !config.manual_config?.okta_app_id
    && !config.entra_app_id
    && !config.manual_config?.entra_app_id;
  const grantType = String(config.grant_type || config.manual_config?.grant_type || '').toLowerCase();
  const requestsEntraOnApprove = !!(
    config.manual_config?.request_entra_create_on_approve ||
    config.manual_config?.request_entra_app_create ||
    config.manual_config?.vysor_cc_create_request ||
    (config.sso_type === 'OIDC' && grantType === 'client_credentials' && !config.client_id && !config.manual_config?.client_id)
  );
  const showEntraCreateOnApprove =
    isReviewable &&
    requestsEntraOnApprove &&
    !config.client_id &&
    !config.manual_config?.client_id;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SecurityIcon color="primary" fontSize="small" />
          <Typography fontWeight={700}>
            {isReviewable ? 'Review SSO Configuration' : 'SSO Configuration Details'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        {siblingPending > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {siblingPending === 1
              ? 'Another pending request already exists for this application. Opening this dialog does not change either request.'
              : `${siblingPending} other pending requests already exist for this application. Opening this dialog does not change them.`}
          </Alert>
        )}
        {/* Summary chips */}
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
            <Chip label={config.application_name} size="small" color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
            <Chip label={config.instance_name || 'Instance'} size="small" variant="outlined" />
            <Chip label={config.sso_type} size="small" sx={{ ...protocolChipSx(config.sso_type, mode), fontWeight: 600 }} />
            <Chip {...statusChipProps(config.status)} size="small" sx={{ fontWeight: 600, fontSize: UI_TOKENS.text.helper }} />
            {showEntraCreateOnApprove && (
              <Chip
                label="Entra create on approve"
                size="small"
                color="info"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: UI_TOKENS.text.helper }}
              />
            )}
          </Stack>
          <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.secondary' }}>
            Submitted by <strong>{config.submitted_by_name || '—'}</strong> on {formatDateTime(config.created_at)}
          </Typography>
        </Box>

        {/* Progress stepper */}
        <Box sx={{ px: 1, py: 1.5, mb: 2, bgcolor: 'action.hover', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <SSOProgressStepper status={config.status} />
        </Box>

        {/* Entra create-on-approve — CC submitted without client ID */}
        {showEntraCreateOnApprove && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Entra App Registration pending SSO approval.</strong> No Application (client) ID yet —
            approving will create the Entra app and attach the client ID (and secret) during provisioning.
          </Alert>
        )}

        {/* Provision CTA — shown when Approved but not yet provisioned */}
        {needsProvisioning && (
          <Alert
            severity="warning"
            sx={{ mb: 2, alignItems: 'center' }}
            action={
              onProvision && (
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  startIcon={<CloudUploadIcon />}
                  disabled={provisioning}
                  onClick={() => onProvision(config.id)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {provisioning ? 'Provisioning…' : 'Provision to IdP'}
                </Button>
              )
            }
          >
            <strong>Approved — awaiting IdP provisioning.</strong> The app has not been created in the IdP yet.
          </Alert>
        )}

        {/* Config Details — shared component shows platform, attributes, Okta app id, metadata */}
        <Box sx={{ mb: 2 }}>
          <SSOConfigCompactDetails config={config} appRiskLevel={config.risk_level} />
        </Box>

        {/* Security Policy overlay when mapping is known */}
        {config.risk_level && ['Low', 'Medium', 'High', 'Critical'].includes(config.risk_level) && riskPolicy && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', display: 'block', mb: 1 }}>
              Mapped IdP Policy
            </Typography>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: 1.5,
              border: `1px solid ${getRiskSurface(config.risk_level, mode).fg}66`,
              backgroundColor: getRiskSurface(config.risk_level, mode).bg,
            }}>
              <Chip label={config.risk_level} size="small" sx={{ fontSize: UI_TOKENS.text.helper, fontWeight: 700, height: 18, backgroundColor: getRiskSurface(config.risk_level, mode).bg, color: getRiskSurface(config.risk_level, mode).fg, border: '1px solid currentColor' }} />
              <Typography sx={{ fontWeight: 600, fontSize: UI_TOKENS.text.bodySm, color: getRiskSurface(config.risk_level, mode).fg }}>
                {riskPolicy.policy_name}
              </Typography>
              {riskPolicy.policy_type && (
                <Typography sx={{ fontSize: UI_TOKENS.text.caption, color: 'text.secondary' }}>
                  ({riskPolicy.policy_type.replace(/_/g, ' ')})
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* ── Review form (always visible for reviewable, collapsible for non-reviewable) ── */}
        {!isReviewable && (
          <Box sx={{ mt: 1, mb: 1 }}>
            <Button
              size="small"
              variant="text"
              color="inherit"
              endIcon={<ExpandMoreIcon sx={{ transform: showUpdateReview ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
              onClick={() => setShowUpdateReview((v) => !v)}
              sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.secondary', textTransform: 'none', pl: 0 }}
            >
              {showUpdateReview ? 'Hide review form' : 'Update review / assignment'}
            </Button>
          </Box>
        )}
        <Collapse in={isReviewable || showUpdateReview}>
          <Divider sx={{ mb: 2 }} />
          <FormControl size="small" fullWidth sx={{ mb: 1.5, '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm }, '& .MuiSelect-select': { fontSize: UI_TOKENS.text.bodySm } }}>
            <InputLabel>Assign To</InputLabel>
            <Select value={assignedTo} label="Assign To" onChange={(e) => setAssignedTo(e.target.value)}>
              <MenuItem value="" sx={{ fontSize: UI_TOKENS.text.bodySm }}>Unassigned</MenuItem>
              {ssoAdmins.map((a) => (
                <MenuItem key={a.id} value={a.id} sx={{ fontSize: UI_TOKENS.text.bodySm }}>{a.first_name} {a.last_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth sx={{ mb: 1.5, '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm }, '& .MuiSelect-select': { fontSize: UI_TOKENS.text.bodySm } }}>
            <InputLabel>Decision</InputLabel>
            <Select value={decision} label="Decision" displayEmpty onChange={(e) => setDecision(e.target.value as any)}>
              <MenuItem value="" sx={{ fontSize: UI_TOKENS.text.bodySm }}>Choose a decision</MenuItem>
              <MenuItem value="In Progress" sx={{ fontSize: UI_TOKENS.text.bodySm }}>In Progress — mark as being reviewed</MenuItem>
              <MenuItem value="Approved" disabled={!riskDefined || !assignedTo} sx={{ fontSize: UI_TOKENS.text.bodySm }}>
                {!assignedTo
                  ? 'Approve — assign someone first'
                  : riskDefined
                    ? 'Approve — provision SSO'
                    : 'Approve — set a risk score first'}
              </MenuItem>
              <MenuItem value="Rejected" sx={{ fontSize: UI_TOKENS.text.bodySm }}>Reject</MenuItem>
              <MenuItem value="Need More Info" sx={{ fontSize: UI_TOKENS.text.bodySm }}>Need More Info — request clarification</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Review Notes"
            multiline
            rows={3}
            fullWidth
            size="small"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes visible to the applicant…"
            InputProps={{ sx: { fontSize: UI_TOKENS.text.bodySm } }}
            InputLabelProps={{ sx: { fontSize: UI_TOKENS.text.bodySm } }}
          />
        </Collapse>
      </DialogContent>

      <Box sx={{ px: 3, pb: 1 }}>
        <Button
          size="small"
          variant="outlined"
          disabled={checkingOut}
          onClick={() => {
            setCheckingOut(true);
            setCheckoutError('');
            setCheckedOutSecret('');
            void ssoAPI.checkoutClientSecret(config.id)
              .then((response) => {
                setCheckedOutSecret(response.data.data?.client_secret || '');
              })
              .catch((error: { response?: { data?: { error?: string } } }) => {
                setCheckoutError(error.response?.data?.error || 'The vault did not release this secret.');
              })
              .finally(() => setCheckingOut(false));
          }}
        >
          {checkingOut ? 'Checking out…' : 'Check out client secret'}
        </Button>
        <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary', mt: 0.5 }}>
          Reads the vault and writes an audit event. The IdP token is not part of this checkout.
        </Typography>
        {checkoutError && (
          <Alert severity="warning" sx={{ mt: 1 }}>{checkoutError}</Alert>
        )}
        {checkedOutSecret && (
          <TextField
            label="Client secret"
            fullWidth
            size="small"
            type="password"
            value={checkedOutSecret}
            sx={{ mt: 1 }}
            InputProps={{ readOnly: true }}
            helperText="Close this dialog to clear the secret from the page."
          />
        )}
      </Box>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {decision === 'Approved' && (
          <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: 'text.secondary', mr: 'auto' }}>
            Submit creates a {config.sso_type || 'SSO'} application in the connected identity provider. Opening this dialog does not.
          </Typography>
        )}
        <Button onClick={onClose} disabled={submitting || provisioning}>Close</Button>
        {(isReviewable || showUpdateReview) && (
          <Button
            variant="contained"
            disabled={submitting || provisioning || !decision || (decision === 'Approved' && (!riskDefined || !assignedTo))}
            startIcon={decision === 'Approved' ? <CheckCircleIcon /> : decision === 'Rejected' ? <CancelIcon /> : <EditIcon />}
            color={decision === 'Approved' ? 'success' : decision === 'Rejected' ? 'error' : 'primary'}
            onClick={() => { if (decision) void onSubmit(config.id, decision, notes, assignedTo); }}
          >
            {submitting ? 'Saving…' : 'Submit Decision'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const SSOReview: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const pipelineView = searchParams.get('pipeline');
  const muiTheme = useMuiTheme();
  const mode = muiTheme.palette.mode;

  const [configs, setConfigs] = useState<SSOConfig[]>([]);
  const [statistics, setStatistics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [ssoAdmins, setSsoAdmins] = useState<SSOAdmin[]>([]);

  // Filters
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [ssoTypeFilter, setSsoTypeFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [instanceFilter, setInstanceFilter] = useState('all');
  const [myWorkloadOnly, setMyWorkloadOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [listingSort, setListingSort] = useState<ListingSortValue>(DEFAULT_LISTING_SORT);
  const [visibleColumns, setVisibleColumns] = useState<ColumnKey[]>(DEFAULT_VISIBLE_COLUMNS);
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState<null | HTMLElement>(null);

  // OIDC credentials dialog
  const [oidcDialogOpen, setOidcDialogOpen] = useState(false);
  const [oidcCredentials, setOidcCredentials] = useState<{
    clientId: string;
    clientSecret?: string;
    oidcDiscoveryEndpoint: string;
    clientSecretExpiresAt?: string;
    grantType: 'authorization_code' | 'client_credentials' | 'authorization_code_pkce';
    connectionName: string;
  } | null>(null);

  // Review dialog
  const [reviewOpen, setReviewOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<SSOConfig | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [provisioning, setProvisioning] = useState(false);

  const authHeaders = useCallback(
    () => getAuthHeaders({ 'Content-Type': 'application/json' }),
    []
  );

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await fetch(`${API_ROOT}/sso/configurations/enterprise`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Failed to load configurations (${res.status})`);
      const data = await res.json();
      setConfigs(data.data || []);
    } catch (err: any) {
      setFetchError(err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  const fetchSSOAdmins = useCallback(async () => {
    try {
      const res = await fetch(`${API_ROOT}/sso/admins`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setSsoAdmins(data.data || []);
    } catch { /* silent */ }
  }, [authHeaders]);

  const fetchStatistics = useCallback(async () => {
    try {
      const response = await ssoAPI.getStatistics();
      setStatistics(response.data.data);
    } catch (err) {
      console.error('Error loading SSO statistics:', err);
    }
  }, []);

  useEffect(() => {
    fetchConfigs();
    fetchSSOAdmins();
    fetchStatistics();
  }, [fetchConfigs, fetchSSOAdmins, fetchStatistics]);

  const openedConfigRef = useRef<string | null>(null);
  useEffect(() => {
    const configId = searchParams.get('config');
    if (!configId || openedConfigRef.current === configId || configs.length === 0) return;
    const match = configs.find((row) => row.id === configId);
    if (!match) return;
    openedConfigRef.current = configId;
    setSelectedConfig(match);
    setReviewOpen(true);
  }, [configs, searchParams]);

  const reviewDueLabel = (createdAt: string) => {
    const due = new Date(createdAt);
    if (Number.isNaN(due.getTime())) return '';
    due.setDate(due.getDate() + 7);
    return `Due ${due.toLocaleDateString()}`;
  };

  const handleTake = async (config: SSOConfig) => {
    if (!user?.id) return;
    await ssoAPI.assignConfiguration(config.id, user.id);
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username;
    setConfigs((prev) => prev.map((row) => (
      row.id === config.id ? { ...row, assigned_to: user.id, assigned_to_name: name } : row
    )));
  };

  const handleTakeUnassignedPlatform = async () => {
    if (!user?.id || ssoTypeFilter === 'all') return;
    const pending = ['Pending Review', 'In Progress', 'Need More Info'];
    const targets = configs.filter((row) =>
      !row.assigned_to &&
      row.sso_type === ssoTypeFilter &&
      pending.includes(row.status)
    );
    if (targets.length === 0) return;
    const confirmed = window.confirm(`Take ${targets.length} unassigned ${ssoTypeFilter} request${targets.length === 1 ? '' : 's'}? Status stays the same.`);
    if (!confirmed) return;
    for (const row of targets) {
      await ssoAPI.assignConfiguration(row.id, user.id);
    }
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username;
    const ids = new Set(targets.map((row) => row.id));
    setConfigs((prev) => prev.map((row) => (
      ids.has(row.id) ? { ...row, assigned_to: user.id, assigned_to_name: name } : row
    )));
  };

  const handleSubmitReview = useCallback(
    async (configId: string, decision: string, notes: string, assignedTo: string) => {
      setSubmitting(true);
      try {
        const res = await fetch(`${API_ROOT}/sso/configurations/${configId}/status`, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify({
            status: decision,
            review_notes: notes,
            assigned_to: assignedTo || null,
            reviewed_by: user?.id,
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body?.error || `Request failed (${res.status})`);
        }
        setReviewOpen(false);
        setSelectedConfig(null);
        setSuccessMsg(
          decision === 'Approved'
            ? 'SSO configuration approved successfully.'
            : decision === 'Rejected'
            ? 'SSO configuration rejected.'
            : 'Review saved successfully.'
        );
        await fetchConfigs();
        await fetchStatistics();
      } catch (err: any) {
        setFetchError(err.message);
      } finally {
        setSubmitting(false);
      }
    },
    [authHeaders, user, fetchConfigs, fetchStatistics]
  );

  const handleProvision = useCallback(
    async (configId: string) => {
      setProvisioning(true);
      try {
        const res = await fetch(`${API_ROOT}/sso/configurations/${configId}/provision`, {
          method: 'POST',
          headers: authHeaders(),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok || !body.success) {
          throw new Error(body?.message || body?.error || `Provision failed (${res.status})`);
        }
        const msg = body.idp === 'okta'
          ? 'App successfully created in Okta. Refresh to see credentials.'
          : 'Provisioning started — check back in ~20 seconds.';
        setSuccessMsg(msg);
        setReviewOpen(false);
        setSelectedConfig(null);
        await fetchConfigs();
        await fetchStatistics();
      } catch (err: any) {
        setFetchError(err.message);
      } finally {
        setProvisioning(false);
      }
    },
    [authHeaders, fetchConfigs, fetchStatistics]
  );

  // Filtering
  const filtered = configs.filter((c) => {
    const q = searchText.toLowerCase();
    if (
      q &&
      !(
        c.application_name?.toLowerCase().includes(q) ||
        c.connection_name?.toLowerCase().includes(q) ||
        c.instance_name?.toLowerCase().includes(q) ||
        c.submitted_by_name?.toLowerCase().includes(q) ||
        c.assigned_to_name?.toLowerCase().includes(q)
      )
    ) {
      return false;
    }
    if (pipelineView === 'pending' && !['Pending Review', 'In Progress', 'Need More Info'].includes(c.status || '')) return false;
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    if (ssoTypeFilter !== 'all' && c.sso_type !== ssoTypeFilter) return false;
    if (assigneeFilter === 'unassigned' && c.assigned_to) return false;
    if (assigneeFilter !== 'all' && assigneeFilter !== 'unassigned' && c.assigned_to !== assigneeFilter) return false;
    if (instanceFilter !== 'all' && c.instance_id !== instanceFilter) return false;
    if (myWorkloadOnly && user?.id && c.assigned_to !== user.id) return false;
    return true;
  });

  useEffect(() => {
    setPage(1);
  }, [searchText, statusFilter, ssoTypeFilter, assigneeFilter, instanceFilter, myWorkloadOnly, rowsPerPage, listingSort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * rowsPerPage;
  const paginatedFiltered = sortListing(filtered, listingSort, {
    getName: (c) => c.application_name || c.instance_name || '',
    getDate: (c) => c.created_at,
  }).slice(startIndex, startIndex + rowsPerPage);

  const instanceOptions = Array.from(
    new Map(
      configs
        .filter((c) => c.instance_id)
        .map((c) => [c.instance_id, c.instance_name || c.instance_id.slice(0, 8)])
    ).entries()
  ).sort((a, b) => String(a[1]).localeCompare(String(b[1])));

  const activeColumns = ALL_COLUMNS.filter((c) => visibleColumns.includes(c.key));

  const toggleColumn = (key: ColumnKey) => {
    const col = ALL_COLUMNS.find((c) => c.key === key);
    if (col?.alwaysOn) return;
    setVisibleColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const showCol = (key: ColumnKey) => visibleColumns.includes(key);

  const handleShowOIDC = (config: SSOConfig) => {
    if (config.sso_type !== 'OIDC' || !config.client_id) return;
    setOidcCredentials({
      clientId: config.client_id,
      clientSecret: config.client_secret || config.manual_config?.client_secret,
      oidcDiscoveryEndpoint:
        config.oidc_discovery_endpoint ||
        config.manual_config?.oidc_discovery_endpoint ||
        '',
      clientSecretExpiresAt:
        config.client_secret_expires_at || config.manual_config?.client_secret_expires_at,
      grantType: (config.grant_type ||
        config.manual_config?.grant_type ||
        'authorization_code') as 'authorization_code' | 'client_credentials' | 'authorization_code_pkce',
      connectionName: config.connection_name || config.application_name,
    });
    setOidcDialogOpen(true);
  };

  const handleRenewedSecret = (
    configId: string,
    result: { client_secret?: string; client_secret_expires_at?: string }
  ) => {
    setConfigs((prev) =>
      prev.map((c) =>
        c.id === configId
          ? {
              ...c,
              ...(result.client_secret ? { client_secret: result.client_secret } : {}),
              ...(result.client_secret_expires_at
                ? { client_secret_expires_at: result.client_secret_expires_at }
                : {}),
              manual_config: {
                ...(c.manual_config || {}),
                ...(result.client_secret ? { client_secret: result.client_secret } : {}),
                ...(result.client_secret_expires_at
                  ? { client_secret_expires_at: result.client_secret_expires_at }
                  : {}),
              },
            }
          : c
      )
    );
    if (selectedConfig?.id === configId) {
      setSelectedConfig({
        ...selectedConfig,
        ...(result.client_secret ? { client_secret: result.client_secret } : {}),
        ...(result.client_secret_expires_at
          ? { client_secret_expires_at: result.client_secret_expires_at }
          : {}),
      });
    }
    const expiryLabel = formatSecretExpiry(result.client_secret_expires_at);
    setSuccessMsg(
      result.client_secret_expires_at
        ? `Client secret renewed. New expiry: ${expiryLabel}`
        : 'Client secret renewed successfully.'
    );
    void fetchConfigs();
  };

  const canRenew = (config: SSOConfig) =>
    config.sso_type === 'OIDC' &&
    !!config.client_id &&
    (user?.role === 'sso_admin' || user?.role === 'super_admin');

  const isReviewable = (status: string) =>
    ['Pending Review', 'In Progress', 'Need More Info'].includes(status);

  const MENU_SX = {
    PaperProps: {
      sx: {
        '& .MuiMenuItem-root': {
          fontSize: UI_TOKENS.text.bodySm,
          minHeight: UI_TOKENS.menu.itemMinHeight,
          py: UI_TOKENS.menu.itemPaddingY,
        },
      },
    },
  };
  const INPUT_SX = {
    '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm },
    '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm },
    '& .MuiSelect-select': { fontSize: UI_TOKENS.text.bodySm },
  };
  const uniqueApplicationCount = new Set(configs.map((config) => config.application_id)).size;
  const onboardedApplicationCount = new Set(
    configs
      .filter((config) => config.status === 'Completed')
      .map((config) => config.application_id)
  ).size;
  const pipeline = statistics?.pipeline;
  const metricTiles = [
    {
      label: 'SSO Pipeline',
      value: pipeline?.total ?? uniqueApplicationCount,
      color: 'primary.main',
      sub: 'Onboarded plus pending',
    },
    {
      label: 'SSO Onboarded',
      value: pipeline?.completed ?? onboardedApplicationCount,
      color: 'success.main',
      sub: 'Approved or completed',
    },
    {
      label: 'Pending',
      value:
        pipeline?.pending ??
        configs.filter((config) =>
          ['Pending Review', 'In Progress', 'Need More Info'].includes(config.status)
        ).length,
      color: 'warning.main',
      sub: 'Pending review, in progress, or need more info',
    },
    {
      label: 'Approved',
      value:
        pipeline?.approved ??
        configs.filter((config) => config.status === 'Approved').length,
      color: 'info.main',
      sub: 'Included in onboarded',
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
        <SecurityIcon sx={{ color: 'primary.main', fontSize: 22 }} />
        <Box>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, lineHeight: 1.2 }}>SSO Review</Typography>
          <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.secondary' }}>
            {pipelineView === 'pending'
              ? 'Pending review, in progress, and need more info. This is the same pending number as the dashboard.'
              : 'Review, provision, and manage SSO configurations across all application instances.'}
          </Typography>
        </Box>
      </Box>

      {/* Banners */}
      {successMsg && (
        <Alert severity="success" onClose={() => setSuccessMsg(null)} sx={{ mb: 2, py: 0.5, fontSize: UI_TOKENS.text.bodySm }}>
          {successMsg}
        </Alert>
      )}
      {fetchError && (
        <Alert severity="error" sx={{ mb: 2, py: 0.5, fontSize: UI_TOKENS.text.bodySm }} action={
          <Button color="inherit" size="small" onClick={fetchConfigs} sx={{ fontSize: UI_TOKENS.text.buttonSm }}>Retry</Button>
        }>
          {fetchError}
        </Alert>
      )}

      {/* SSO analytics */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {metricTiles.map((tile) => (
          <Grid item xs={6} sm={3} key={tile.label}>
            <Card
              sx={{
                p: 2,
                textAlign: 'center',
                borderRadius: 2,
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  display: 'block',
                  mb: 0.5,
                }}
              >
                {tile.label}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: tile.color, lineHeight: 1.2 }}>
                {tile.value}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>
                {tile.sub}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, p: '10px 14px' }}>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <FilterListIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
            <TextField
              size="small"
              placeholder="Search application, instance, connection, assignee…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 0.5, color: 'text.secondary', fontSize: 16 }} />, sx: { fontSize: UI_TOKENS.text.bodySm } }}
              InputLabelProps={{ sx: { fontSize: UI_TOKENS.text.bodySm } }}
              sx={{ minWidth: 260 }}
            />
            <FormControl size="small" sx={{ minWidth: 150, ...INPUT_SX }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)} MenuProps={MENU_SX}>
                {STATUS_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120, ...INPUT_SX }}>
              <InputLabel>SSO Type</InputLabel>
              <Select value={ssoTypeFilter} label="SSO Type" onChange={(e) => setSsoTypeFilter(e.target.value)} MenuProps={MENU_SX}>
                {SSO_TYPE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150, ...INPUT_SX }}>
              <InputLabel>Assignee</InputLabel>
              <Select value={assigneeFilter} label="Assignee" onChange={(e) => setAssigneeFilter(e.target.value)} MenuProps={MENU_SX}>
                <MenuItem value="all">All Assignees</MenuItem>
                <MenuItem value="unassigned">Unassigned</MenuItem>
                {ssoAdmins.map((a) => (
                  <MenuItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150, ...INPUT_SX }}>
              <InputLabel>Instance</InputLabel>
              <Select value={instanceFilter} label="Instance" onChange={(e) => setInstanceFilter(e.target.value)} MenuProps={MENU_SX}>
                <MenuItem value="all">All Instances</MenuItem>
                {instanceOptions.map(([id, name]) => (
                  <MenuItem key={id} value={id}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <ListingSortSelect
              value={listingSort}
              onChange={setListingSort}
              sx={INPUT_SX}
              menuProps={MENU_SX}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={myWorkloadOnly}
                  onChange={(e) => setMyWorkloadOnly(e.target.checked)}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PersonOutlineIcon sx={{ fontSize: 16 }} />
                  <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm }}>My workload</Typography>
                </Box>
              }
              sx={{ ml: 0, mr: 0 }}
            />
            {myWorkloadOnly && (
              <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary' }}>
                Assigned to you. Review due is 7 days after the request was submitted.
              </Typography>
            )}
            {ssoTypeFilter !== 'all' && (user?.role === 'sso_admin' || user?.role === 'super_admin') && (
              <Button size="small" variant="outlined" onClick={() => void handleTakeUnassignedPlatform()}>
                {`Take unassigned ${ssoTypeFilter}`}
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<ViewColumnIcon sx={{ fontSize: '1rem !important' }} />}
              onClick={(e) => setColumnsMenuAnchor(e.currentTarget)}
              sx={{ textTransform: 'none', fontSize: UI_TOKENS.text.buttonSm }}
            >
              Columns
            </Button>
            <Menu
              anchorEl={columnsMenuAnchor}
              open={Boolean(columnsMenuAnchor)}
              onClose={() => setColumnsMenuAnchor(null)}
              PaperProps={{ sx: { minWidth: 200 } }}
            >
              {ALL_COLUMNS.map((col) => (
                <MenuItem
                  key={col.key}
                  dense
                  disabled={col.alwaysOn}
                  onClick={() => toggleColumn(col.key)}
                >
                  <Checkbox size="small" checked={visibleColumns.includes(col.key)} disabled={col.alwaysOn} />
                  <ListItemText
                    primary={col.label}
                    primaryTypographyProps={{ fontSize: UI_TOKENS.text.bodySm }}
                  />
                </MenuItem>
              ))}
            </Menu>
            <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.secondary', ml: 'auto' }}>
              {filtered.length} instance{filtered.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
      </Paper>

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 340px)', overflowX: 'auto' }}>
          <Table stickyHeader size="small" sx={{ ...listingTableSx, tableLayout: 'fixed', minWidth: 1100, width: '100%' }}>
            <TableHead>
              <TableRow>
                {activeColumns.map(({ key, label, width }) => (
                  <TableCell
                    key={key}
                    sx={{
                      width, fontWeight: 700, fontSize: UI_TOKENS.text.helper, textTransform: 'uppercase',
                      letterSpacing: 0.5, color: 'text.secondary', bgcolor: 'grey.50',
                      borderBottom: '1px solid', borderColor: 'divider', whiteSpace: 'nowrap', py: 1,
                      overflow: 'hidden', textOverflow: 'ellipsis',
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(6)].map((_, i) => (
                  <TableRow key={i}>
                    {activeColumns.map((col) => (
                      <TableCell key={col.key}><Skeleton height={28} /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={activeColumns.length} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      No SSO configuration requests match the current filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedFiltered.map((config, idx) => {
                  const sp = statusChipProps(config.status);
                  const expiry =
                    config.client_secret_expires_at ||
                    config.manual_config?.client_secret_expires_at ||
                    config.certificate_expiration_date;
                  return (
                    <TableRow
                      key={config.id}
                      hover
                      sx={{
                        bgcolor: idx % 2 === 0 ? 'background.paper' : 'grey.50',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      {showCol('date') && (
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.secondary' }}>
                            {formatDate(config.created_at)}
                          </Typography>
                          <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary' }}>
                            {reviewDueLabel(config.created_at)}
                          </Typography>
                        </TableCell>
                      )}

                      {showCol('application') && (
                        <TableCell>
                          <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 600 }} noWrap>
                            {config.application_name || '—'}
                          </Typography>
                        </TableCell>
                      )}

                      {showCol('instance') && (
                        <TableCell>
                          <Typography sx={{ fontSize: UI_TOKENS.text.bodySm }} noWrap>
                            {config.instance_name || config.instance_id?.slice(0, 8) || '—'}
                          </Typography>
                        </TableCell>
                      )}

                      {showCol('environment') && (
                        <TableCell>
                          {config.environment ? (
                            <Chip
                              label={config.environment}
                              size="small"
                              sx={{
                                height: 20, fontSize: UI_TOKENS.text.helper, fontWeight: 600,
                                ...environmentChipSx(config.environment, mode),
                              }}
                            />
                          ) : (
                            <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.disabled' }}>—</Typography>
                          )}
                        </TableCell>
                      )}

                      {showCol('ssoType') && (
                        <TableCell>
                          <Chip
                            label={config.sso_type}
                            size="small"
                            sx={{
                              height: 20, fontSize: UI_TOKENS.text.helper, fontWeight: 600,
                              ...protocolChipSx(config.sso_type, mode),
                            }}
                          />
                        </TableCell>
                      )}

                      {showCol('connection') && (
                        <TableCell>
                          <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: config.connection_name ? 'text.primary' : 'text.disabled', fontStyle: config.connection_name ? 'normal' : 'italic' }} noWrap>
                            {config.connection_name || '—'}
                          </Typography>
                        </TableCell>
                      )}

                      {showCol('requestedBy') && (
                        <TableCell>
                          <Typography sx={{ fontSize: UI_TOKENS.text.bodySm }} noWrap>
                            {config.submitted_by_name || '—'}
                          </Typography>
                        </TableCell>
                      )}

                      {showCol('assignedTo') && (
                        <TableCell>
                          <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: config.assigned_to_name ? 'text.primary' : 'text.disabled', fontStyle: config.assigned_to_name ? 'normal' : 'italic' }} noWrap>
                            {config.assigned_to_name || 'Unassigned'}
                          </Typography>
                        </TableCell>
                      )}

                      {showCol('secretExpires') && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.secondary', minWidth: 72 }}>
                              {formatSecretExpiry(expiry)}
                            </Typography>
                            {canRenew(config) && (
                              <RenewClientSecretControl
                                configId={config.id}
                                applicationName={config.application_name}
                                connectionName={config.connection_name}
                                currentExpiresAt={expiry}
                                onRenewed={(result) => handleRenewedSecret(config.id, result)}
                                onError={(message) => setFetchError(message)}
                              />
                            )}
                          </Box>
                        </TableCell>
                      )}

                      {showCol('status') && (
                        <TableCell>
                          <Chip
                            label={sp.label}
                            color={sp.color}
                            variant={sp.variant}
                            size="small"
                            sx={{ height: 22, fontSize: UI_TOKENS.text.helper, fontWeight: 600, ...sp.sx }}
                          />
                        </TableCell>
                      )}

                      {showCol('actions') && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            {config.sso_type === 'OIDC' && config.client_id && (
                              <Tooltip title="View OIDC credentials">
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => handleShowOIDC(config)}
                                  sx={{ p: 0.5 }}
                                >
                                  <InfoIcon sx={{ fontSize: '1rem' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {!config.assigned_to && isReviewable(config.status) && (user?.role === 'sso_admin' || user?.role === 'super_admin') && (
                              <Button size="small" variant="text" onClick={() => void handleTake(config)} sx={{ fontSize: UI_TOKENS.text.labelXs, py: 0.25, px: 1 }}>
                                Take
                              </Button>
                            )}
                            <Tooltip title={isReviewable(config.status) ? 'Review configuration' : 'View configuration'}>
                              <Button
                                size="small"
                                variant={isReviewable(config.status) ? 'outlined' : 'text'}
                                color="primary"
                                startIcon={isReviewable(config.status) ? <EditIcon sx={{ fontSize: '0.9rem !important' }} /> : <VisibilityIcon sx={{ fontSize: '0.9rem !important' }} />}
                                onClick={() => { setSelectedConfig(config); setReviewOpen(true); }}
                                sx={{ fontSize: UI_TOKENS.text.labelXs, py: 0.25, px: 1 }}
                              >
                                {isReviewable(config.status) ? 'Review' : 'View'}
                              </Button>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <ListingPagination
          page={safePage}
          totalPages={totalPages}
          totalItems={filtered.length}
          pageSize={rowsPerPage}
          onPageChange={setPage}
          onPageSizeChange={setRowsPerPage}
          pageSizeSelectSx={INPUT_SX}
          pageSizeMenuProps={MENU_SX}
        />
      </Paper>

      {/* Review Dialog */}
      <ReviewDialog
        open={reviewOpen}
        config={selectedConfig}
        ssoAdmins={ssoAdmins}
        onClose={() => { setReviewOpen(false); setSelectedConfig(null); }}
        onSubmit={handleSubmitReview}
        onProvision={handleProvision}
        submitting={submitting}
        provisioning={provisioning}
        siblingPending={
          selectedConfig
            ? configs.filter((row) =>
                row.id !== selectedConfig.id &&
                row.application_id === selectedConfig.application_id &&
                ['Pending Review', 'In Progress', 'Need More Info'].includes(row.status)
              ).length
            : 0
        }
      />

      {oidcCredentials && (
        <OIDCCredentialsDialog
          open={oidcDialogOpen}
          onClose={() => {
            setOidcDialogOpen(false);
            setOidcCredentials(null);
          }}
          credentials={oidcCredentials}
        />
      )}
    </Box>
  );
};

export default SSOReview;
